import { useContext, useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';

import { DataContext } from '../../App';
import { ButtonType, AppRoute } from '../../types/enums';
import Button from '../Button/Button';
import Input from '../Input/Input';
import MultiSelect from '../Input/MultiSelect';
import { Dashboard, SaveDashboard, Website } from '../../types/types';

interface DashboardFormProps {
  onSave: SaveDashboard;
  initialValues?: Dashboard;
}

const DashboardForm = ({
  onSave,
  initialValues = {
    name: '',
    websites: [] as Dashboard['websites'],
    description: ''
  } as Dashboard
}: DashboardFormProps) => {
  const { websites, refreshWebsites } = useContext(DataContext);
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description ?? '');
  const [selected, setSelected] = useState(initialValues.websites?.map((it) => it.id) ?? []);
  const [nameError, setNameError] = useState(false);
  const [descError, setDescError] = useState(false);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (name.trim().length === 0) {
      setNameError(true);

      return;
    }

    if (name.trim().length > 180) {
      setDescError(true);

      return;
    }

    onSave({ name: name.trim(), description: description.trim(), websiteIds: selected });
  };

  useEffect(() => {
    if (!websites || websites.length === 0) {
      refreshWebsites();
    }
  }, []);

  return (
    <form onSubmit={onSubmit}>
      <Input
        id="name"
        label="Dashboard Name:"
        setValue={setName}
        value={name}
        helperText="Publicly visible name. We will display this at the top of your dashboard."
        placeholder="e.g. Your Dashboard"
        error={nameError ? 'This field is required!' : ''}
      />
      <Input
        id="description"
        label="Dashboard Description:"
        setValue={setDescription}
        value={description}
        helperText="Description for internal usage only. This will not be displayed on the dashboard."
        placeholder="e.g. My favorite websites"
        error={descError ? 'Description cannot be longer than 180 characters!' : ''}
      />
      <MultiSelect
        options={getWebsiteOptions(websites)}
        checkedValues={selected}
        setValue={(value, checked) => {
          if (checked) {
            setSelected((s) => [...s, value]);
          } else {
            setSelected((s) => s.filter((it) => it !== value));
          }
        }}
        title="Websites:"
        helperText="Pick a few websites that you want to display on this dashboard. Don't stress too much over it, you can add and remove websites later too."
      />
      <div className="flex">
        <Button isSubmit type={ButtonType.PRIMARY}>
          SAVE DASHBOARD
        </Button>
        <NavLink to={AppRoute.DASHBOARDS}>
          <Button type={ButtonType.GHOST}>Cancel</Button>
        </NavLink>
      </div>
    </form>
  );
};

const getWebsiteOptions = (sites: Website[]) => sites.map(({ id, name }) => ({ value: id, name }));

export default DashboardForm;
